import React, { useState } from "react";
import {
  Datagrid,
  TextField,
  ShowButton,
  EditButton,
  useTranslate,
  FunctionField,
  ReferenceField,
  ReferenceArrayField,
  SingleFieldList,
  ChipField,
  ReferenceInput,
  SelectInput,
  ReferenceArrayInput,
  SelectArrayInput,
  useDataProvider,
  useNotify,
  useListContext,
  SimpleForm,
  useRefresh,
} from "react-admin";
import { JsonField } from "react-admin-json-view";
import {
  DateField,
  CreateOnlyTopToolbar,
  CustomList,
  EmptyMessage,
} from "../../../shared/components/index.js";
import ClientFilter from "../ClientFilter.js";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";

const CustomBulkUpdateButton = ({
  selectedIds = [],
  onUnselectItems,
  resource,
  merchant,
}) => {
  const [open, setOpen] = useState(false);
  const [defaultFormValues, setDefaultFormValues] = useState({});
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const t = useTranslate();

  const handleClickOpen = async () => {
    try {
      setOpen(true);

      const { data: existingClients } = await dataProvider.getMany(resource, {
        ids: selectedIds,
      });

      if (selectedIds.length === 1) {
        const tagIds = existingClients
          .map((client) => client.tag_ids || [])
          .reduce((common, tags, index) => {
            if (index === 0) return tags;
            return common.filter((tag) => tags.includes(tag));
          }, []);

        const [categoryId] = existingClients.map(
          (client) => client.category_id || ""
        );

        return setDefaultFormValues({
          category_id: categoryId,
          tag_ids: tagIds,
        });
      } else if (selectedIds.length > 1) {
        const categoryIds = existingClients.map(
          (client) => client.category_id || ""
        );
        const allSameCategory = categoryIds.every(
          (id) => id === categoryIds[0]
        );

        const commonTagIds = existingClients
          .map((client) => client.tag_ids || [])
          .reduce((common, tags) => {
            return common.filter((tag) => tags.includes(tag));
          }, existingClients[0]?.tag_ids || []);

        return setDefaultFormValues({
          ...(allSameCategory && { category_id: categoryIds[0] }),
          tag_ids: commonTagIds,
        });
      } else {
        return setDefaultFormValues({});
      }
    } catch (error) {
      notify("resources.notifications.errors.Forbidden", { type: "error" });
    }
  };

  const handleClose = () => {
    setDefaultFormValues({});
    onUnselectItems?.();
    setOpen(false);
    refresh();
  };

  const handleSubmit = async (formData) => {
    try {
      const { data: existingClients } = await dataProvider.getMany(resource, {
        ids: selectedIds,
      });

      const isSingleClient = selectedIds.length === 1;

      const allTags = existingClients.map((client) => client.tag_ids || []);
      const commonTags = allTags.reduce((common, tags) => {
        return common.filter((tag) => tags.includes(tag));
      }, allTags[0] || []);

      const formTags = Array.isArray(formData.tag_ids) ? formData.tag_ids : [];

      const categoryIds = existingClients.map(
        (client) => client.category_id || ""
      );
      const allSameCategory = categoryIds.every((id) => id === categoryIds[0]);

      const formCategoryId = formData.category_id;

      await Promise.all(
        existingClients.map((client) => {
          const clientTags = client.tag_ids || [];

          // TAGS logic
          let newTagIds;

          if (isSingleClient) {
            newTagIds = formTags;
          } else {
            const tagsToRemove = commonTags.filter(
              (tag) => !formTags.includes(tag)
            );
            const tagsToAdd = formTags.filter(
              (tag) => !clientTags.includes(tag)
            );

            newTagIds = clientTags.filter((tag) => !tagsToRemove.includes(tag));
            newTagIds.push(...tagsToAdd);
            newTagIds = [...new Set(newTagIds)];
          }

          // CATEGORY logic
          let category_id;

          if (isSingleClient) {
            category_id = formCategoryId || null;
          } else {
            if (formCategoryId !== "") {
              // New category was selected
              category_id = formCategoryId;
            } else if (allSameCategory) {
              // User wants to clear shared category
              category_id = null;
            } else {
              // Don't touch differing categories
              category_id = client.category_id || "";
            }
          }

          const updateData = {
            category_id,
            tag_ids: newTagIds,
            phone: client.phone,
            status: client.status,
            unconfirmed_changes: [],
          };

          return dataProvider.update(resource, {
            id: client.id,
            data: updateData,
          });
        })
      );

      notify("resources.notifications.success");
      handleClose();
    } catch (error) {
      notify("resources.notifications.errors.Forbidden", { type: "error" });
      handleClose();
    }
  };

  return (
    <>
      <Button
        onClick={handleClickOpen}
        disabled={selectedIds.length === 0}
        className="button-edit"
      >
        {t("buttons.edit")}
      </Button>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{t("buttons.edit")}</DialogTitle>
        <DialogContent sx={{ minWidth: 400, pt: 2 }}>
          <SimpleForm
            onSubmit={handleSubmit}
            toolbar={false}
            defaultValues={defaultFormValues}
          >
            <ReferenceInput
              label="Category"
              source="category_id"
              reference="Category"
              filter={{ merchant_id: merchant.id }}
              fullWidth
            >
              <SelectInput
                optionText="name"
                optionValue="id"
                fullWidth
                resettable
              />
            </ReferenceInput>
            <ReferenceArrayInput
              label="resources.Client.fields.tags"
              source="tag_ids"
              reference="Tag"
              filter={{ merchant_id: merchant.id }}
            >
              <SelectArrayInput
                optionText="name"
                optionValue="id"
                fullWidth
                resettable="true"
              />
            </ReferenceArrayInput>
            <DialogActions>
              <Button onClick={handleClose}>
                {t("buttons.bulk_update_cancel")}
              </Button>
              <Button type="submit" variant="contained">
                {t("buttons.bulk_update_save")}
              </Button>
            </DialogActions>
          </SimpleForm>
        </DialogContent>
      </Dialog>
    </>
  );
};

CustomBulkUpdateButton.propTypes = {
  selectedIds: PropTypes.arrayOf(PropTypes.any),
  onUnselectItems: PropTypes.func,
  resource: PropTypes.string.isRequired,
  merchant: PropTypes.any.isRequired,
};

const BulkActionWrapper = ({ resource, merchant }) => {
  const { selectedIds, onUnselectItems } = useListContext();

  return (
    <CustomBulkUpdateButton
      selectedIds={selectedIds}
      onUnselectItems={onUnselectItems}
      resource={resource}
      merchant={merchant}
    />
  );
};

BulkActionWrapper.propTypes = {
  resource: PropTypes.string.isRequired,
  merchant: PropTypes.shape({
    id: PropTypes.any.isRequired,
  }).isRequired,
};

const ListClient = (props) => {
  const t = useTranslate();
  const merchantId = localStorage.getItem("id");

  return (
    <CustomList
      {...props}
      filters={<ClientFilter />}
      actions={<CreateOnlyTopToolbar />}
    >
      <Datagrid
        empty={<EmptyMessage />}
        rowClick={false}
        bulkActionButtons={
          <BulkActionWrapper resource="Client" merchant={{ id: merchantId }} />
        }
      >
        <TextField source="phone" />
        <TextField source="email" />
        <TextField source="first_name" />
        <ReferenceField source="category_id" reference="Category" link="show">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceArrayField source="tag_ids" reference="Tag" sortable={false}>
          <SingleFieldList linkType="show">
            <ChipField source="name" size="small" />
          </SingleFieldList>
        </ReferenceArrayField>
        <FunctionField
          source="status"
          render={(record) =>
            t(`resources.Client.source.status.${record.status}`)
          }
        />
        <JsonField
          reference="Changes"
          source="unconfirmed_changes"
          label="resources.Client.fields.unconfirmed_changes.name"
          reactJsonOptions={{
            name: null,
            collapsed: true,
            enableClipboard: false,
            displayDataTypes: false,
          }}
          sortable={false}
        />
        <DateField source="created_at" />
        <DateField source="updated_at" />
        <FunctionField
          source="blocked_at"
          render={(record) =>
            record?.blocked_at ? (
              <DateField source="blocked_at" record={record} />
            ) : (
              t("")
            )
          }
          label="resources.Client.source.blocked_at"
        />
        <FunctionField
          source="deleted_at"
          render={(record) =>
            record?.deleted_at ? (
              <DateField source="deleted_at" record={record} />
            ) : (
              t("")
            )
          }
          label="resources.Client.source.deleted"
        />
        <ShowButton className="button-show" />
        <EditButton className="button-edit" />
      </Datagrid>
    </CustomList>
  );
};

export default ListClient;
